/**
 * Channels Store
 */

import { useCallback } from 'react';
import { createStore } from '@commandDrivenStateEngine/core/createStore';
import type { CommandMetaInput, Dispatch } from '@commandDrivenStateEngine/core/types';
import { ClientLibraryType } from '@bett3r-dev/teselly-client-library';
import { SystemIntegrationConnectorReadmodel } from '@bett3r-dev/teselly-domain';
import useAuth from '@contexts/useAuth/useAuth';
import { ConnectorEntry } from '../../shared/state/state';
import { initialState, selectors } from './state';
import { handlers } from './handlers';
import { invariants } from './invariants';
import { ChannelsCommand } from './commands';
import {
  createChannelsSyncPolicy,
  createChannelAutoImportPolicy,
  createChannelStatsPolicy,
  createChannelsSubmitPolicy,
  createChannelsOAuthPolicy,
  createChannelsDeletePolicy,
  createChannelDetailLoadPolicy,
  createChannelBillingEntitiesSyncPolicy,
  createChannelDetailSaveBillingPolicy,
  createChannelDetailSaveDefaultsPolicy,
  createChannelDetailLoadRetailStoreReferenceDataPolicy,
  createChannelDetailSaveRetailStoreConfigPolicy,
  createChannelStatusMappingLoadPolicy,
  createChannelStatusMappingSavePolicy,
  createCategoriesPolicy
} from './policies';

const store = createStore({
  name: 'channels',
  initialState,
  handlers,
  invariants
});

export const {
  useSelector: useChannelsSelector,
  useErrors: useChannelsErrors,
  usePolicies: useChannelsPolicies,
  getState: getChannelsState,
  dispatch: dispatchChannelsCommand
} = store;

/**
 * Dispatch hook that attaches the authenticated user to user-sourced commands
 * so the `canApply` capability gates can authorize them. The engine core stays
 * auth-agnostic; this web-app boundary supplies the actor. An explicit
 * `meta.user` (e.g. from tests) is left untouched.
 */
export const useChannelsDispatch = (): Dispatch<ChannelsCommand> => {
  const auth = useAuth();
  const user = auth?.user ?? null;
  return useCallback<Dispatch<ChannelsCommand>>(
    ( command, metaInput: CommandMetaInput ) =>
      store.dispatch(
        command,
        metaInput.source === 'user' && metaInput.user === undefined
          ? { ...metaInput, user }
          : metaInput
      ),
    [user]
  );
};

/**
 * Policy factory - creates policies that require backend and shared state access
 * 
 * @param backend - Client library instance for API calls
 * @param getConnectors - Function to get current connectors from shared state
 */
export const createChannelsPolicies = (
  backend: ClientLibraryType,
  getConnectors: () => Record<string, ConnectorEntry>
) => [
  // Core policies. The sync policy reads connectors structurally;
  // `ConnectorEntry` is the FE-extended type (adds `connectorKind`+`capabilities`)
  // and doesn't structurally overlap with the bare readmodel type, so cast
  // through `unknown` at the boundary.
  createChannelsSyncPolicy(
    backend,
    getConnectors as unknown as () => Record<string, SystemIntegrationConnectorReadmodel>
  ),
  createChannelsSubmitPolicy( backend, getConnectors ),
  // TV1-2354 — fires the one-shot publication import for a channel connected with
  // "importar publicaciones automáticamente" ticked. Registered after the sync
  // policy it depends on for channel arrival.
  createChannelAutoImportPolicy( backend ),
  // TV1-2361 — category coverage + publication counts for the list cards.
  createChannelStatsPolicy( backend ),
  createChannelsOAuthPolicy( backend ),
  createChannelsDeletePolicy( backend ),
  // Detail view policies
  createChannelDetailLoadPolicy( backend ),
  // Live billing-entity catalog (subscription) — supersedes the one-shot load that
  // went stale when the in-page setup wizard created an entity after the view opened.
  createChannelBillingEntitiesSyncPolicy( backend ),
  createChannelDetailSaveBillingPolicy( backend ),
  createChannelDetailSaveDefaultsPolicy( backend ),
  // Status mapping policies (WS-3)
  createChannelStatusMappingLoadPolicy( backend ),
  createChannelStatusMappingSavePolicy( backend ),
  // Import is dispatched imperatively from ChannelInfo.onConfirm (handleConfirmImport)
  // to avoid the close/navigate race that dropped the reactive policy's dispatch.
  // RetailStore policies
  createChannelDetailLoadRetailStoreReferenceDataPolicy( backend ),
  createChannelDetailSaveRetailStoreConfigPolicy( backend ),
  // Category mapper ( subscribe readmodel + map/unmap backend commands + ML search )
  createCategoriesPolicy( backend )
];

export { selectors };

export {
  useImportModalSelector,
  useImportModalDispatch,
  useImportModalPolicies,
  getImportModalState,
  dispatchImportModalCommand
} from './importModal/store';
